import { useState, useEffect, useRef } from "react";
import Button from "../../../../../common/Button/Button";
import * as React from "react";
import dayjs from "dayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

const EditTarget = ({ isOpen, userName, initialValues, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({});
  const modalRef = useRef(null);

  // ✅ FIX: Always sync formData when initialValues change, not just when modal is open
  useEffect(() => {
    if (initialValues) {
      setFormData({
        weight: initialValues.weight || 0,
        steps: initialValues.steps || 0,
        water: initialValues.water || 0,
        sleep: initialValues.sleep || 0,
        sleepStart: initialValues.sleepStart || "22:00",
        sleepEnd: initialValues.sleepEnd || "06:00",
      });
    }
  }, [initialValues]);

  // ✅ Additional: Reset form when modal closes
  useEffect(() => {
    if (!isOpen && initialValues) {
      setFormData({
        weight: initialValues.weight || 0,
        steps: initialValues.steps || 0,
        water: initialValues.water || 0,
        sleep: initialValues.sleep || 0,
        sleepStart: initialValues.sleepStart || "22:00",
        sleepEnd: initialValues.sleepEnd || "06:00",
      });
    }
  }, [isOpen, initialValues]);

  const calculateSleepHours = (start, end) => {
    if (!start || !end) return 0;
    const startTime = dayjs(`2023-01-01T${start}`);
    const endTime = dayjs(`2023-01-01T${end}`);
    const adjustedEnd = endTime.isBefore(startTime)
      ? endTime.add(1, "day")
      : endTime;
    return parseFloat(adjustedEnd.diff(startTime, "minute") / 60).toFixed(2);
  };

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };

    if (field === "sleepStart" || field === "sleepEnd") {
      const newStart = field === "sleepStart" ? value : updated.sleepStart;
      const newEnd = field === "sleepEnd" ? value : updated.sleepEnd;
      const sleepHrs = calculateSleepHours(newStart, newEnd);
      updated.sleep = sleepHrs;
    }

    setFormData(updated);
  };

  const handleSave = () => {
    if (onSubmit) {
      const payload = {
        target_weight_kg: parseFloat(formData.weight) || 0,
        target_step_count: parseInt(formData.steps) || 0,
        target_quantity_ml: parseInt(formData.water) || 0,
        target_sleep_hrs: parseFloat(formData.sleep) || 0,
        // ✅ FIX: Include sleepStart and sleepEnd in the payload
        target_sleep_start: formData.sleepStart,
        target_sleep_end: formData.sleepEnd,
      };
      onSubmit(payload);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-orange-primary bg-opacity-10 backdrop-blur-sm"></div>

      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-2xl relative z-10"
      >
        <div className="flex justify-between items-center ">
          <h2 className="text-2xl font-bold">Target for {userName}</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 text-2xl"
          >
            &#10006;
          </button>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6 [&_label]:text-gray ">
          <div>
            <label className="block mb-1 font-medium ">
              Target Weight (Kgs)
            </label>
            <input
              type="number"
              placeholder="Kg"
              value={formData.weight || ""}
              onChange={(e) => handleChange("weight", e.target.value)}
              className="w-32 p-2 border rounded"
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">Target Steps</label>
            <input
              type="number"
              placeholder="Enter Step Count"
              value={formData.steps || ""}
              onChange={(e) => handleChange("steps", e.target.value)}
              className="w-32 p-2 border rounded"
            />
          </div>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div>
              <label className="block mb-1 font-medium">
                Target Sleep Start
              </label>
              <div className="w-32">
                <TimePicker
                  label="Sleep Start"
                  value={dayjs(`2023-01-01T${formData.sleepStart || "22:00"}`)}
                  onChange={(newValue) =>
                    handleChange("sleepStart", newValue.format("HH:mm"))
                  }
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                    seconds: renderTimeViewClock,
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block mb-1 font-medium">Target Sleep End</label>
              <div className="w-32">
                <TimePicker
                  label="Sleep End"
                  value={dayjs(`2023-01-01T${formData.sleepEnd || "06:00"}`)}
                  onChange={(newValue) =>
                    handleChange("sleepEnd", newValue.format("HH:mm"))
                  }
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                    seconds: renderTimeViewClock,
                  }}
                />
              </div>
            </div>

            <div className="text-sm text-gray-600 col-span-2">
              Sleep Duration: {formData?.sleep || 0} hrs
            </div>
          </LocalizationProvider>

          <div className="col-span-2">
            <label className="block mb-1 font-medium">Target Water (ml)</label>
            <input
              type="number"
              placeholder="Enter in ml"
              value={formData.water || ""}
              onChange={(e) => handleChange("water", e.target.value)}
              className="w-32 p-2 border rounded"
            />
          </div>
        </div>

        <div className="flex justify-center">
          <Button
            text="Save Target"
            onClick={handleSave}
            className="bg-orange-primary hover:bg-dark-orange text-white font-semibold py-2 px-6 transition"
          />
        </div>
      </div>
    </div>
  );
};

export default EditTarget;
